import { z } from "zod";
import {
  DERIVED_DATA_TYPES,
  DerivedDataType,
  isDerivedDataType,
  POINT_CLOUD_DATA_TYPES,
  PointCloudDataType,
} from "./dataType";
import { positionLib } from "./position";
import { Corner } from "./rectangle";
import invariant from "tiny-invariant";
import * as turf from "@turf/turf";
import { MapShape } from "../doc";
import { arrayLib } from "@larki/shared/array";
import { geometryLib } from "./geometry";

const SHAPE_TYPES = ["polygon", "path", "rectangle", "extents"] as const;
export const shapeTypeSchema = z.enum(SHAPE_TYPES);
export type ShapeType = z.infer<typeof shapeTypeSchema>;

export type ShapeVertex = {
  type: ShapeType;
  coordinates: GeoJSON.Position;
};

const _baseStateShapeDataTypeSchema = z.union([
  z.object({
    dataType: z
      .enum([...POINT_CLOUD_DATA_TYPES, "unknown", "custom"])
      .default("unknown"),
  }),
  z.object({
    dataType: z.enum(DERIVED_DATA_TYPES),
    baseShapeId: z.string(),
  }),
  z.object({
    dataType: z.literal("_2d_plan_on_map"),
    planS3Url: z.string(),
    // Links the plan back to its asset. Optional so pre-existing plan shapes still parse.
    assetId: z.string().optional(),
  }),
]);
const _baseStateShapeSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable().optional(),
    customOrderId: z.string().nullable().optional(),
    color: z.tuple([z.number(), z.number(), z.number()]).optional(),
  })
  .and(_baseStateShapeDataTypeSchema);
export const positionSchema = z
  .string()
  .transform((val) => positionLib.fromString(val));
const _baseStatePolygonSchema = z.object({
  type: z.literal(shapeTypeSchema.enum.polygon),
  exterior: z.array(positionSchema),
  holes: z.array(z.array(positionSchema)).optional(),
});
const statePolygonSchema = _baseStateShapeSchema.and(_baseStatePolygonSchema);
const _baseStateRectangleSchema = z.object({
  type: z.literal(shapeTypeSchema.enum.rectangle),
  exterior: z.object({
    [Corner.sw]: positionSchema,
    [Corner.se]: positionSchema,
    [Corner.ne]: positionSchema,
    [Corner.nw]: positionSchema,
  }),
  holes: z.array(z.array(positionSchema)).optional(),
});
const stateRectangleSchema = _baseStateShapeSchema.and(
  _baseStateRectangleSchema,
);
const _baseStatePathSchema = z.object({
  type: z.literal(shapeTypeSchema.enum.path),
  vertices: z.array(positionSchema),
});
const statePathSchema = _baseStateShapeSchema.and(_baseStatePathSchema);
const _baseStateExtentsSchema = z.object({
  type: z.literal(shapeTypeSchema.enum.extents),
  extents: z.array(positionSchema),
});
const stateExtentsSchema = _baseStateShapeSchema.and(_baseStateExtentsSchema);
export const stateShapeSchema = z.union([
  statePolygonSchema,
  stateRectangleSchema,
  statePathSchema,
  stateExtentsSchema,
]);

const _baseTentativeShapeSchema = z.object({
  id: z.string(),
});
const tentativePolygonSchema = _baseTentativeShapeSchema.extend({
  type: z.literal(shapeTypeSchema.enum.polygon),
  exterior: z.array(positionSchema),
});
const tentativeRectangleSchema = _baseTentativeShapeSchema.extend({
  type: z.literal(shapeTypeSchema.enum.rectangle),
  initial: positionSchema,
  final: positionSchema.optional(),
});
const tentativePathSchema = _baseTentativeShapeSchema.extend({
  type: z.literal(shapeTypeSchema.enum.path),
  vertices: z.array(positionSchema),
});
const tentativeBooleanSchema = _baseTentativeShapeSchema.extend({
  type: z.literal("boolean"),
  parent_index: z.number(),
  vertices: z.array(positionSchema),
});
export const tentativeShapeSchema = z.discriminatedUnion("type", [
  tentativePolygonSchema,
  tentativeRectangleSchema,
  tentativePathSchema,
  tentativeBooleanSchema,
]);

export const mapShapeInputSchema = z
  .object({
    name: z.string().nullable().optional(),
    customOrderId: z.string().nullable().optional(),
    color: z.tuple([z.number(), z.number(), z.number()]).optional(),
  })
  .and(
    z.union([
      _baseStateShapeDataTypeSchema.and(_baseStatePolygonSchema),
      _baseStateShapeDataTypeSchema.and(_baseStateRectangleSchema),
      _baseStateShapeDataTypeSchema.and(_baseStatePathSchema),
      _baseStateShapeDataTypeSchema.and(_baseStateExtentsSchema),
    ]),
  );
export type MapShapeInput = z.infer<typeof mapShapeInputSchema>;
export type DerivedMapShapeInput = MapShapeInput & {
  dataType: DerivedDataType;
  baseShapeId: string;
};

export type BaseTentativeShape = z.infer<typeof _baseTentativeShapeSchema>;
export type TentativePolygon = z.infer<typeof tentativePolygonSchema>;
export type TentativeRectangle = z.infer<typeof tentativeRectangleSchema>;
export type TentativePath = z.infer<typeof tentativePathSchema>;
export type TentativeBoolean = z.infer<typeof tentativeBooleanSchema>;
export type TentativeShape = z.infer<typeof tentativeShapeSchema>;
export type TentativeShapeType = TentativeShape["type"];
export type BaseStateShape = z.infer<typeof _baseStateShapeSchema>;
export type StatePolygon = z.infer<typeof statePolygonSchema>;
export type StateRectangle = z.infer<typeof stateRectangleSchema>;
export type StatePath = z.infer<typeof statePathSchema>;
export type StateExtents = z.infer<typeof stateExtentsSchema>;
export type StateShape =
  | StatePolygon
  | StateRectangle
  | StatePath
  | StateExtents;
export type StateShapeType = StateShape["type"];
export type DerivedStateShape = StateShape & {
  dataType: DerivedDataType;
  baseShapeId: string;
};

export type SmartSelection = {
  coverage: {
    product?: {
      product_id: number;
      category_name: PointCloudDataType;
      display_name: string;
      delivery_method: "automatic" | "manual";
    };
  };
  selection: {
    geometry: GeoJSON.Polygon;
    raw?: GeoJSON.Polygon | GeoJSON.LineString;
  };
};

export function isDerivedShape(shape: StateShape): shape is DerivedStateShape {
  return isDerivedDataType(shape.dataType);
}

export function isDerivedShapeInput(
  shape: MapShapeInput,
): shape is DerivedMapShapeInput {
  return isDerivedDataType(shape.dataType);
}

export function convertSmartSelectionsToMapShapeInput(
  selections: SmartSelection[],
  deliveryMethod?: "automatic" | "manual",
) {
  return selections
    .map((smartSelection): MapShapeInput | null => {
      const {
        coverage: { product },
        selection,
      } = smartSelection;
      if (!product) return null;
      if (deliveryMethod && product.delivery_method !== deliveryMethod)
        return null;
      let shape: MapShapeInput | null = null;
      if (selection.raw?.type === "LineString") {
        shape = {
          type: "path",
          dataType: product.category_name,
          vertices: selection.raw.coordinates,
        };
      } else if (selection.geometry.type === "Polygon") {
        shape = {
          type: "polygon",
          dataType: product.category_name,
          exterior: selection.geometry.coordinates[0].slice(0, -1),
          holes: selection.geometry.coordinates
            .slice(1)
            .map((hole) => hole.slice(0, -1)),
        };
      }
      return shape;
    })
    .filter((shape): shape is MapShapeInput => shape !== null);
}

export function oppositeCornersToRectangle(
  initial: GeoJSON.Position,
  final: GeoJSON.Position,
): Record<Corner, GeoJSON.Position> {
  const finalEastEdge = final[0] > initial[0];
  // const finalNorthEdge = final[1] > initial[1];
  let [left, right] = [initial, final];
  if (!finalEastEdge) {
    [left, right] = [final, initial];
  }
  invariant(left[0] <= right[0]);
  let [sw, nw, ne, se]: GeoJSON.Position[] = [];
  if (left[1] <= right[1]) {
    // left is south-west, right is north-east
    sw = left;
    ne = right;
    nw = [sw[0], ne[1]];
    se = [ne[0], sw[1]];
  } else {
    // left is north-west, right is south-east
    nw = left;
    se = right;
    sw = [nw[0], se[1]];
    ne = [se[0], nw[1]];
  }
  invariant(sw && nw && ne && se);
  return {
    [Corner.sw]: sw,
    [Corner.nw]: nw,
    [Corner.ne]: ne,
    [Corner.se]: se,
  };
}

export interface ClosestResult<T, C> {
  xyDistanceSquared: number;
  coordinate: C;
  value: T;
}

const squaredDistance = (a: [number, number], b: [number, number]) => {
  return (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
};

function getClosestPosition({
  targetCoord,
  points,
}: {
  targetCoord: [number, number];
  points: ([number, number] | null)[];
}) {
  let current: ClosestResult<number, [number, number]> | null = null;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (!p) continue;
    const xyDistanceSquared = squaredDistance(targetCoord, p);
    if (!current || xyDistanceSquared < current.xyDistanceSquared) {
      current = {
        xyDistanceSquared,
        coordinate: p,
        value: i,
      };
    }
  }
  return current;
}

function getClosestPositionNested({
  targetCoord,
  points,
}: {
  targetCoord: [number, number];
  points: ([number, number] | null)[][];
}) {
  let current: ClosestResult<[number, number], [number, number]> | null = null;
  for (let i = 0; i < points.length; i++) {
    for (let j = 0; j < points[i].length; j++) {
      const p = points[i][j];
      if (!p) continue;
      const xyDistanceSquared = squaredDistance(targetCoord, p);
      if (!current || xyDistanceSquared < current.xyDistanceSquared) {
        current = {
          xyDistanceSquared,
          coordinate: p,
          value: [i, j],
        };
      }
    }
  }
  return current;
}

function getClosestOverall<T, C>(
  ...closestResults: (ClosestResult<T, C> | null)[]
) {
  return closestResults.reduce(
    (prev, curr) =>
      curr && (!prev || curr.xyDistanceSquared < prev.xyDistanceSquared)
        ? curr
        : prev,
    null,
  );
}

function getMidpoints(points: GeoJSON.Position[]) {
  const midpoints: GeoJSON.Position[] = [];
  for (let i = 0; i < points.length; i++) {
    const before = points[i];
    const after = points[(i + 1) % points.length];
    midpoints.push(turf.midpoint(before, after).geometry.coordinates);
  }
  return midpoints;
}

function getBoundingBox(shape: MapShape) {
  if (shape.type === "polygon") {
    return turf.bbox(turf.polygon([[...shape.exterior, shape.exterior[0]]]));
  } else if (shape.type === "rectangle") {
    return turf.bbox(
      turf.polygon([
        [
          shape.exterior.sw,
          shape.exterior.se,
          shape.exterior.ne,
          shape.exterior.nw,
          shape.exterior.sw,
        ],
      ]),
    );
  } else if (shape.type === "path") {
    const feat = pathFeatureFromSequence(shape.vertices, {}, 3);
    if (!feat) return null;
    return turf.bbox(feat);
  }
  return null;
}

export type MapShapeWithRenderGeometry = MapShape & {
  renderGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
};

function shapeBooleanIntersects(a: ShapeInput, b: ShapeInput) {
  const aGeom = extractShapeGeometry(a);
  const bGeom = extractShapeGeometry(b);
  if (!aGeom || !bGeom) {
    console.warn("failed to extract underlying geometry");
    return false;
  }
  return turf.booleanIntersects(aGeom, bGeom);
}

function shapeIntersect(...features: ShapeInput[]) {
  const geoms = features
    .map((f) => extractShapeGeometry(f))
    .filter(arrayLib.filterNonNull);
  if (geoms.length === 0) return null;
  if (geoms.length === 1) return geoms[0];
  return turf.intersect(
    turf.featureCollection(geoms.map((g) => turf.feature(g))),
  )?.geometry;
}

function shapeUnion(...features: ShapeInput[]) {
  const geoms = features
    .map((f) => extractShapeGeometry(f))
    .filter(arrayLib.filterNonNull);
  if (geoms.length === 0) return null;
  if (geoms.length === 1) return geoms[0];
  return geometryLib.union(
    geoms.map((g) => turf.feature(g)),
    {},
  )?.geometry;
}

function shapeContains(shape: ShapeInput, point: GeoJSON.Position) {
  const geom = extractShapeGeometry(shape);
  if (!geom) return false;
  return turf.booleanPointInPolygon(point, turf.feature(geom));
}

function extractFeatureFromPolygonOrRectangle<T>(
  shape: StatePolygon | StateRectangle | StateExtents,
  properties?: T,
  isHoleExcluded?: boolean,
) {
  const exterior =
    shape.type === "polygon"
      ? [...shape.exterior, shape.exterior[0]]
      : shape.type === "extents"
        ? [...shape.extents, shape.extents[0]]
        : [
            shape.exterior.sw,
            shape.exterior.se,
            shape.exterior.ne,
            shape.exterior.nw,
            shape.exterior.sw,
          ];
  const exteriorPoly = turf.polygon(
    [[...exterior, exterior[0]]],
    properties ?? undefined,
  );

  if (isHoleExcluded) {
    // return the shapes full coordinates so we
    // can compute the exact centroid of the shape w/o reducing the boolean/hole
    return exteriorPoly.geometry;
  }

  let diff: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
  if (shape.type === "extents") {
    diff = exteriorPoly;
  } else {
    const validHoles = shape.holes?.filter((h) => h.length >= 3);
    diff = validHoles?.length
      ? (turf.difference(
          turf.featureCollection([
            exteriorPoly,
            ...validHoles.map((h) => turf.polygon([[...h, h[0]]])),
          ]),
        ) ?? exteriorPoly)
      : exteriorPoly;
  }
  return diff.geometry;
}

export type ShapeInput =
  | MapShape
  | GeoJSON.Polygon
  | GeoJSON.MultiPolygon
  | GeoJSON.Feature<GeoJSON.Polygon>
  | GeoJSON.Feature<GeoJSON.MultiPolygon>;

function extractShapeGeometry<T extends GeoJSON.GeoJsonProperties>(
  shape: ShapeInput,
  properties?: T,
  isHoleExcluded?: boolean,
) {
  if (shape.type === "Feature") return shape.geometry;
  if (shape.type === "Polygon" || shape.type === "MultiPolygon") return shape;
  if (shape.type === "path")
    return (
      pathFeatureFromSequence(shape.vertices, properties, 3)?.geometry ?? null
    );
  return extractFeatureFromPolygonOrRectangle(
    shape,
    properties,
    isHoleExcluded,
  );
}

const pathFeatureFromSequence = <T extends GeoJSON.GeoJsonProperties>(
  seq: GeoJSON.Position[],
  properties: T = {} as T,
  sectorSteps = 5,
) => {
  invariant(seq.length >= 2);
  const PATH_WIDTH_METERS = 20;
  const MIN_DISTANCE_TO_SHOW_FINAL_SECTOR_METERS = 1;

  const elements: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, T>[] =
    [];
  for (let i = 0; i < seq.length - 1; i++) {
    const p1 = turf.point(seq[i]);
    const p2 = turf.point(seq[i + 1]);
    if (i == seq.length - 2) {
      const dst = turf.distance(p1, p2, { units: "meters" });
      if (dst < MIN_DISTANCE_TO_SHOW_FINAL_SECTOR_METERS) {
        continue;
      }
    }
    const bearing = turf.bearing(p1, p2);
    const p0a = turf.destination(p1, PATH_WIDTH_METERS, bearing + 90, {
      units: "meters",
    });
    const p0b = turf.destination(p1, PATH_WIDTH_METERS, bearing - 90, {
      units: "meters",
    });
    const p1a = turf.destination(p2, PATH_WIDTH_METERS, bearing + 90, {
      units: "meters",
    });
    const p1b = turf.destination(p2, PATH_WIDTH_METERS, bearing - 90, {
      units: "meters",
    });
    const rect = turf.polygon(
      [
        [
          turf.getCoord(p0a),
          turf.getCoord(p0b),
          turf.getCoord(p1b),
          turf.getCoord(p1a),
          turf.getCoord(p0a),
        ],
      ],
      properties,
    );
    // buffer slightly to ensure overlap happens
    let rectBuffered = turf.buffer(rect, 0.001, {
      units: "meters",
    }) as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, T>;
    invariant(rectBuffered);
    rectBuffered = { ...rectBuffered, properties };

    elements.push(rectBuffered);
    if (i > 0) {
      const p0 = turf.point(seq[i - 1]);
      const priorBearing = turf.bearing(p0, p1);
      // get the signed-angle difference in [-180,180] https://stackoverflow.com/a/61901403
      const diff = ((bearing - priorBearing + 360 + 180) % 360) - 180;
      const _to360 = (angle: number) => (angle + 360) % 360;
      const _to180 = (angle: number) =>
        angle > 180 ? angle - 360 : angle < -180 ? angle + 360 : angle;
      const angle0 = _to360(priorBearing);
      const angle1 = _to360(bearing);
      const a0 = _to180(_to360(diff >= 0 ? angle0 - 90 : angle1 + 90));
      const a1 = _to180(_to360(diff >= 0 ? angle1 - 90 : angle0 + 90));
      let controlPointSector = turf.sector(p1, PATH_WIDTH_METERS, a0, a1, {
        units: "meters",
        steps: sectorSteps,
        properties,
      }) as GeoJSON.Feature<GeoJSON.Polygon, T>;
      // let controlPointSectorBuffered = turf.buffer(controlPointSector, 0.001, {
      //   units: 'meters',
      // }) as GeoJSON.Feature<GeoJSON.Polygon, T>;
      // invariant(controlPointSectorBuffered);
      // controlPointSectorBuffered = {
      //   ...controlPointSectorBuffered,
      //   properties,
      // };
      controlPointSector = {
        ...controlPointSector,
        properties,
      };
      elements.push(controlPointSector);
    }
  }
  if (elements.length === 0) return null;
  let surrounding: GeoJSON.Feature<
    GeoJSON.Polygon | GeoJSON.MultiPolygon,
    T
  > | null;
  try {
    surrounding =
      elements.length === 1
        ? elements[0]
        : geometryLib.union(elements, properties);
    invariant(surrounding);
    surrounding.properties = properties;
  } catch (e) {
    console.error(e);
    for (const element of elements) {
      console.log(element, turf.booleanValid(element));
    }
    console.error(seq);
    throw e;
  }
  return surrounding;
};

export const shapeLib = {
  // convertStateToJson,
  // checkStateIsValid,
  getClosestPosition,
  getClosestPositionNested,
  getClosestOverall,
  getMidpoints,
  getBoundingBox,
  shapeBooleanIntersects,
  shapeIntersect,
  shapeUnion,
  shapeContains,
  extractFeatureFromPolygonOrRectangle,
  pathFeatureFromSequence,
  extractShapeGeometry,
};
